import { toHubChatMessage, typingNamesFrom } from "@/chat/connectors";
import { api, ApiError, humanizeClientError } from "@/lib/api";
import { connectUrl, type ConnectResponse } from "@/lib/googleConnect";

export type ChatProviderId = "slack" | "cliq" | "zoom" | "teams" | "google";

export type ChatAccount = {
  id: string;
  providerId: ChatProviderId;
  /** Workspace / org display name (Slack team, Cliq org). */
  teamName?: string;
  teamId?: string;
  /** Signed-in user email when the provider exposes it. */
  email?: string;
  displayName?: string;
  connected?: boolean;
  /** Provider user id of the connected login (Slack `authed_user.id`) — who "me" is in a thread. */
  userId?: string;
  selfUserId?: string;
  /** "customer": connected through the ATW Hub Slack app (Tip 148) instead of the test app. */
  mode?: "customer";
  /** Customer accounts only: Slack has approved ATW Hub in the Marketplace. */
  listed?: boolean;
  /** What the provider really supports for this account (Tip 169). Missing → treat as false. */
  capabilities?: Partial<ChatCapabilities>;
};

export type ChatCapabilities = {
  send: boolean;
  edit: boolean;
  remove: boolean;
  /** Open and reply to threads inside Hub. */
  threads: boolean;
  /** Add/remove reactions (needs Slack reactions:write). */
  reactions: boolean;
  /** "Open in Slack" links for messages and threads. */
  permalink: boolean;
};

/** Hub only offers an action when the API says the provider can do it. */
export function chatCapabilities(account: ChatAccount | undefined): ChatCapabilities {
  const caps = account?.capabilities;
  if (caps) {
    return {
      send: Boolean(caps.send),
      edit: Boolean(caps.edit),
      remove: Boolean(caps.remove),
      threads: Boolean(caps.threads),
      reactions: Boolean(caps.reactions),
      permalink: Boolean(caps.permalink),
    };
  }
  // Older API builds: sending/editing as before; nothing that would be faked.
  const writable = Boolean(account) && account!.providerId !== "cliq";
  return { send: writable, edit: writable, remove: writable, threads: false, reactions: false, permalink: false };
}

export type ChatProviderStatus = {
  id: ChatProviderId;
  name?: string;
  configured?: boolean;
  connectedCount?: number;
  oauthReady?: boolean;
};

export type ChatStatus = {
  /** True when at least one chat account is linked. */
  connected?: boolean;
  /** Legacy: Slack app credentials present (kept for older API builds). */
  configured?: boolean;
  teamName?: string;
  setupSteps?: string[];
  message?: string;
  providers?: ChatProviderStatus[];
  accounts?: ChatAccount[];
};

export type ChatChannel = {
  id: string;
  name?: string;
  unread?: number;
  kind?: string;
  topic?: string;
  accountId?: string;
  providerId?: ChatProviderId;
  /** DM profile image from the provider; never invented. */
  avatarUrl?: string;
};

export type ChatImage = { url: string; alt?: string };

export type ChatMessage = {
  id: string;
  author: string;
  text: string;
  /** Epoch ms, or null when the provider row has no usable timestamp. */
  at: number | null;
  /** Provider sender id (Slack `U…`) when the API sends it. */
  userId?: string;
  /** Profile image from the provider; never invented. */
  avatarUrl?: string;
  /** Explicit "sent by the connected account" flag from the API. */
  self?: boolean;
  /** Image blocks / GIFs attached to the message (Slack `image` blocks, unfurls). */
  images?: ChatImage[];
  /** Slack message `ts` — the id `chat.update` / `chat.delete` need. */
  ts?: string;
  edited?: boolean;
  /** Online, away, do not disturb, or offline when the connector knows. */
  presence?: "online" | "away" | "dnd" | "offline";
  reactions?: { emoji: string; name?: string; count: number; mine?: boolean }[];
  readBy?: { name: string; userId?: string }[];
  /** Thread parent: provider thread id and reply count. */
  threadTs?: string;
  replyCount?: number;
  /** On a reply: the parent message ts. */
  parentTs?: string;
  files?: { name: string; url: string }[];
  statusText?: string;
  timeZone?: string;
};

/** Someone in a Slack workspace, for starting a DM. */
export type ChatUser = { id: string; name: string; avatarUrl?: string };

export type ChatGif = {
  id: string;
  title: string;
  /** Rendition posted to Slack. */
  url: string;
  /** Small rendition for the picker grid. */
  previewUrl: string;
  width?: number;
  height?: number;
};

/** Pane-1 subfolders under each chat account (Mail-style tree). */
export type ChatFolderId = "channels" | "direct" | "group" | "chats";

export const CHAT_FOLDERS: Record<ChatProviderId, { id: ChatFolderId; label: string }[]> = {
  slack: [
    { id: "channels", label: "Channels" },
    { id: "direct", label: "Direct messages" },
    { id: "group", label: "Group messages" },
  ],
  cliq: [
    { id: "channels", label: "Channels" },
    { id: "chats", label: "Chats" },
  ],
  zoom: [
    { id: "channels", label: "Channels" },
    { id: "direct", label: "Direct messages" },
    { id: "group", label: "Group messages" },
  ],
  teams: [
    { id: "channels", label: "Channels" },
    { id: "direct", label: "Direct messages" },
    { id: "group", label: "Group messages" },
  ],
  google: [
    { id: "channels", label: "Spaces" },
    { id: "direct", label: "Direct messages" },
    { id: "group", label: "Group chats" },
  ],
};

const DIRECT_KINDS = new Set(["direct", "dm", "im"]);
const GROUP_KINDS = new Set([
  "mpim",
  "mpdm",
  "group_dm",
  "group-dm",
  "groupdm",
  "group_message",
  "group_messages",
  "multi",
]);
const CHANNEL_KINDS = new Set(["channel", "public", "private", "private_channel", "group"]);

/** Which pane-1 folder a channel belongs to, from `channel.kind` (unknown kinds → Channels). */
export function chatFolderForChannel(
  providerId: ChatProviderId,
  channel: ChatChannel,
): ChatFolderId {
  const kind = String(channel.kind || "").trim().toLowerCase();
  if (providerId === "cliq") {
    if (!kind || CHANNEL_KINDS.has(kind)) return "channels";
    return "chats";
  }
  if (providerId === "zoom" || providerId === "teams" || providerId === "google") {
    if (DIRECT_KINDS.has(kind)) return "direct";
    if (GROUP_KINDS.has(kind)) return "group";
    return "channels";
  }
  if (DIRECT_KINDS.has(kind)) return "direct";
  if (GROUP_KINDS.has(kind)) return "group";
  return "channels";
}

export { connectUrl };
export type { ConnectResponse };

/** Workspace first: the same person usually signs in to several Slack workspaces. */
export function chatAccountLabel(account: ChatAccount): string {
  return (
    account.teamName ||
    account.displayName ||
    account.email ||
    (account.providerId === "cliq"
      ? "Zoho Cliq"
      : account.providerId === "zoom"
        ? "Zoom Team Chat"
        : account.providerId === "teams"
          ? "Microsoft Teams"
          : account.providerId === "google"
            ? "Google Chat"
            : "Slack")
  );
}

export function providerConfigured(
  status: ChatStatus | null | undefined,
  providerId: ChatProviderId,
): boolean {
  const row = status?.providers?.find((p) => p.id === providerId);
  if (row) return Boolean(row.configured ?? row.oauthReady);
  if (providerId === "slack") return Boolean(status?.configured);
  return false;
}

export function accountsForProvider(
  accounts: ChatAccount[],
  providerId: ChatProviderId,
): ChatAccount[] {
  return accounts.filter((a) => a.providerId === providerId && a.connected !== false);
}

export async function fetchChatStatus(): Promise<ChatStatus> {
  return api<ChatStatus>("/api/chat/status");
}

export async function fetchChatAccounts(): Promise<ChatAccount[]> {
  const payload = await api<{ accounts?: ChatAccount[] } | ChatAccount[]>(
    "/api/chat/accounts",
  ).catch(() => [] as ChatAccount[]);
  return Array.isArray(payload) ? payload : payload.accounts || [];
}

export async function fetchChatChannels(accountId?: string): Promise<ChatChannel[]> {
  const qs = accountId ? `?accountId=${encodeURIComponent(accountId)}` : "";
  const payload = await api<{ channels?: ChatChannel[] } | ChatChannel[]>(
    `/api/chat/channels${qs}`,
  );
  return Array.isArray(payload) ? payload : payload.channels || [];
}

function pickString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

function parseMessageTime(...values: unknown[]): number | null {
  for (const value of values) {
    if (value == null || value === "") continue;
    const asNumber = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(asNumber) && asNumber > 0) {
      // Slack `ts` is epoch seconds ("1727600000.000100"); Cliq sends epoch ms.
      return asNumber > 1e12 ? asNumber : asNumber * 1000;
    }
    if (typeof value === "string") {
      const parsed = Date.parse(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

const SLACK_TS = /^\d+\.\d+$/;

export function normalizeChatMessage(raw: unknown, index: number): ChatMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const nested = (key: string) =>
    row[key] && typeof row[key] === "object" ? (row[key] as Record<string, unknown>) : undefined;
  const user = nested("user");
  const sender = nested("sender");
  const text = pickString(row.text, row.body, row.message, row.content);
  const author = pickString(
    row.author,
    row.userName,
    row.user_name,
    row.username,
    row.senderName,
    row.sender_name,
    user?.name,
    user?.displayName,
    user?.real_name,
    sender?.name,
    row.from,
    typeof row.user === "string" ? row.user : undefined,
  );
  const at = parseMessageTime(
    row.at,
    row.sentAt,
    row.ts,
    row.time,
    row.timestamp,
    row.createdAt,
    row.date,
  );
  const id = pickString(row.id, row.ts, row.messageId, row.msgid) || `msg-${index}`;
  const userProfile = nested("user_profile");
  const userId = pickString(
    row.userId,
    row.user_id,
    row.senderId,
    user?.id,
    sender?.id,
    typeof row.user === "string" && /^[UW][A-Z0-9]{6,}$/.test(row.user) ? row.user : undefined,
  );
  const avatarUrl = pickString(
    row.avatarUrl,
    row.avatar,
    row.image,
    user?.image_72,
    user?.avatarUrl,
    userProfile?.image_72,
    sender?.avatarUrl,
    sender?.avatar,
  );
  const images = normalizeImages(row.images);
  const selfFlag = [row.self, row.isSelf, row.fromMe, row.mine].find((v) => typeof v === "boolean");
  // Older Hub API builds only encode ts in the row id (`slack_<channel>_<ts>`).
  const ts =
    typeof row.ts === "string" && SLACK_TS.test(row.ts) ? row.ts : id.match(/_(\d+\.\d+)$/)?.[1];
  const hub = toHubChatMessage(raw, index);
  if (!hub && !images.length) return null;
  const message: ChatMessage = {
    id: hub?.id || id,
    author: hub?.author || author || "Unknown",
    text: hub?.text || text,
    at: hub?.at ?? at,
  };
  if (hub?.userId || userId) message.userId = hub?.userId || userId;
  const photo = hub?.avatarUrl || avatarUrl;
  if (/^https:\/\//i.test(photo)) message.avatarUrl = photo;
  if (typeof (hub?.self ?? selfFlag) === "boolean") message.self = hub?.self ?? (selfFlag as boolean);
  if (images.length) message.images = images;
  if (hub?.ts || ts) message.ts = hub?.ts || ts;
  if (hub?.edited || row.edited === true) message.edited = true;
  if (hub?.presence) message.presence = hub.presence;
  if (hub?.reactions) message.reactions = hub.reactions;
  if (hub?.readBy) message.readBy = hub.readBy;
  if (hub?.files) message.files = hub.files;
  if (hub?.statusText) message.statusText = hub.statusText;
  if (hub?.timeZone) message.timeZone = hub.timeZone;
  if (hub?.threadTs) message.threadTs = hub.threadTs;
  if (hub?.replyCount) message.replyCount = hub.replyCount;
  if (hub?.parentTs) message.parentTs = hub.parentTs;
  return message;
}

function normalizeImages(raw: unknown): ChatImage[] {
  if (!Array.isArray(raw)) return [];
  const out: ChatImage[] = [];
  for (const item of raw) {
    const url =
      typeof item === "string"
        ? item
        : item && typeof item === "object"
          ? pickString((item as Record<string, unknown>).url, (item as Record<string, unknown>).image_url)
          : "";
    if (!/^https:\/\//i.test(url) || out.some((img) => img.url === url)) continue;
    const alt =
      item && typeof item === "object"
        ? pickString((item as Record<string, unknown>).alt, (item as Record<string, unknown>).alt_text)
        : "";
    out.push(alt ? { url, alt } : { url });
  }
  return out;
}

/** Provider user id for the connected login, when the account record exposes it. */
export function chatSelfUserId(account: ChatAccount | undefined): string {
  return pickString(account?.userId, account?.selfUserId);
}

/**
 * Whether a message was sent by the connected account (drawn on the right).
 * Order: explicit API flag → sender id vs account user id → the account record's own
 * provider display name (Slack `users.info` real_name stored at connect time).
 */
export function isSelfChatMessage(message: ChatMessage, account: ChatAccount | undefined): boolean {
  if (typeof message.self === "boolean") return message.self;
  if (!account) return false;
  const selfId = chatSelfUserId(account);
  if (selfId && message.userId) return message.userId === selfId;
  if (account.providerId === "zoom") {
    const email = account.email?.trim().toLowerCase();
    const sender = message.userId?.trim().toLowerCase() || message.author?.trim().toLowerCase();
    return Boolean(email && sender && email === sender);
  }
  if (account.providerId !== "slack") return false;
  const own = account.displayName?.trim();
  return Boolean(own && message.author.trim() === own);
}

/** Slack post — Lightsail `POST /api/chat/channels/:id/messages` with the account's user token. */
export async function sendChatMessage(
  accountId: string,
  channelId: string,
  input: { body: string; imageUrl?: string; imageAlt?: string; threadTs?: string },
): Promise<ChatMessage | null> {
  const payload = await api<unknown>(
    `/api/chat/channels/${encodeURIComponent(channelId)}/messages?accountId=${encodeURIComponent(accountId)}`,
    {
      method: "POST",
      body: JSON.stringify({ accountId, ...input }),
    },
  );
  const row =
    payload && typeof payload === "object" && "message" in payload &&
    (payload as { message?: unknown }).message &&
    typeof (payload as { message?: unknown }).message === "object"
      ? (payload as { message: unknown }).message
      : payload;
  return normalizeChatMessage(row, 0);
}

function messagePath(accountId: string, channelId: string, ts: string): string {
  return `/api/chat/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(ts)}?accountId=${encodeURIComponent(accountId)}`;
}

function messageSubPath(accountId: string, channelId: string, ts: string, sub: string): string {
  return `/api/chat/channels/${encodeURIComponent(channelId)}/messages/${encodeURIComponent(ts)}/${sub}?accountId=${encodeURIComponent(accountId)}`;
}

/** Parent plus replies — Lightsail `GET /api/chat/channels/:id/threads/:ts` (Slack conversations.replies). */
export async function fetchChatThreadReplies(
  accountId: string,
  channelId: string,
  threadTs: string,
): Promise<{ messages: ChatMessage[]; hasMore: boolean }> {
  const payload = await api<{ messages?: unknown[]; hasMore?: boolean }>(
    `/api/chat/channels/${encodeURIComponent(channelId)}/threads/${encodeURIComponent(threadTs)}?accountId=${encodeURIComponent(accountId)}`,
  );
  const messages = (payload.messages || [])
    .map((raw, index) => normalizeChatMessage(raw, index))
    .filter((m): m is ChatMessage => Boolean(m))
    .sort((a, b) => (a.at ?? 0) - (b.at ?? 0));
  return { messages, hasMore: Boolean(payload.hasMore) };
}

/** Slack reactions.add / reactions.remove for the connected account. */
export async function setChatReaction(
  accountId: string,
  channelId: string,
  ts: string,
  name: string,
  on: boolean,
): Promise<void> {
  await api<unknown>(messageSubPath(accountId, channelId, ts, "reactions"), {
    method: on ? "POST" : "DELETE",
    body: JSON.stringify({ accountId, name }),
  });
}

/** Slack chat.getPermalink — opens the message (or its thread) in Slack. */
export async function fetchChatPermalink(accountId: string, channelId: string, ts: string): Promise<string> {
  const payload = await api<{ permalink?: string }>(messageSubPath(accountId, channelId, ts, "permalink"));
  return /^https:\/\//i.test(payload.permalink || "") ? payload.permalink! : "";
}

/** Slack `chat.update` — Lightsail `PATCH /api/chat/channels/:id/messages/:ts`. Own messages only. */
export async function editChatMessage(
  accountId: string,
  channelId: string,
  ts: string,
  body: string,
): Promise<ChatMessage | null> {
  const payload = await api<unknown>(messagePath(accountId, channelId, ts), {
    method: "PATCH",
    body: JSON.stringify({ accountId, body }),
  });
  return normalizeChatMessage(payload, 0);
}

/** Slack `chat.delete` — Lightsail `DELETE /api/chat/channels/:id/messages/:ts`. Own messages only. */
export async function deleteChatMessage(accountId: string, channelId: string, ts: string): Promise<void> {
  await api<unknown>(messagePath(accountId, channelId, ts), { method: "DELETE" });
}

/** People in the account's Slack workspace (connected login excluded) — `GET /api/chat/users`. */
export async function searchChatUsers(accountId: string, query: string): Promise<ChatUser[]> {
  const qs = new URLSearchParams({ accountId });
  if (query.trim()) qs.set("q", query.trim());
  const payload = await api<{ users?: ChatUser[] }>(`/api/chat/users?${qs}`);
  return (payload.users || []).filter((u) => u && u.id && u.name);
}

/**
 * Open (or reuse) a DM, or a group DM for 2+ people — `POST /api/chat/dms` (Slack
 * `conversations.open`). Needs the `im:write` / `mpim:write` user scopes.
 */
export async function openChatDm(accountId: string, userIds: string[]): Promise<ChatChannel> {
  return api<ChatChannel>(`/api/chat/dms?accountId=${encodeURIComponent(accountId)}`, {
    method: "POST",
    body: JSON.stringify({ accountId, userIds }),
  });
}

/** Slack error codes come back verbatim from the Hub API; map the ones people hit. */
export function chatActionError(err: unknown, fallback: string): string {
  const code = err instanceof Error ? err.message.trim() : "";
  switch (code) {
    case "missing_scope":
      return "Slack hasn't given Hub this permission for this login yet. Open Settings → Connections → Chat, choose Connect Another Slack, pick this workspace, and click Allow.";
    case "cant_update_message":
    case "edit_window_closed":
      return "Slack won't let you edit this message.";
    case "cant_delete_message":
    case "channel_not_found":
      return "Slack won't let you change messages in this conversation.";
    case "message_not_found":
      return "That message is already gone in Slack.";
    case "user_not_found":
    case "user_disabled":
      return "That person isn't available in this Slack workspace.";
    default:
      return humanizeClientError(err, fallback);
  }
}

/**
 * Where `GET /api/chat/activity` got its data: Slack search (`search:read`), every
 * conversation's latest message (uncapped installs), or last-read times only
 * (Slack caps message history for apps outside the Slack Marketplace).
 */
export type ChatActivityMode = "search" | "full" | "limited";

export type ChatActivity = {
  id: string;
  /** Epoch ms of the newest message, when known. */
  latestAt: number | null;
  latestTs?: string;
  latestAuthor?: string;
  latestText?: string;
  /** Epoch ms of when the connected account last read the conversation. */
  readAt: number | null;
  unread: boolean;
  /** Messages since the last read (100 means 100 or more); 0 when the API doesn't count them. */
  unreadCount: number;
};

export type ChatActivitySnapshot = {
  accountId: string;
  supported: boolean;
  mode?: ChatActivityMode;
  complete: boolean;
  checked: number;
  total: number;
  /** Slack handle of the connected login, used to trim group DM names. */
  selfHandle?: string;
  /** When the API last finished checking; missing until the first check completes. */
  refreshedAt?: string;
  items: ChatActivity[];
};

function slackTsToMs(value: unknown): number | null {
  const seconds = typeof value === "string" || typeof value === "number" ? Number(value) : NaN;
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
}

/** Latest message + read state per conversation — Lightsail `GET /api/chat/activity`. */
export async function fetchChatActivity(accountId: string): Promise<ChatActivitySnapshot> {
  const raw = await api<Record<string, unknown>>(
    `/api/chat/activity?accountId=${encodeURIComponent(accountId)}`,
  );
  const mode = raw.mode === "search" || raw.mode === "full" || raw.mode === "limited" ? raw.mode : undefined;
  const items: ChatActivity[] = [];
  for (const entry of Array.isArray(raw.items) ? raw.items : []) {
    if (!entry || typeof entry !== "object") continue;
    const row = entry as Record<string, unknown>;
    const id = pickString(row.id);
    if (!id) continue;
    const latestTsRaw = pickString(row.latestTs);
    const latestTs = latestTsRaw && SLACK_TS.test(latestTsRaw) ? latestTsRaw : undefined;
    const latestAtNum = typeof row.latestAt === "number" && Number.isFinite(row.latestAt) ? row.latestAt : null;
    const readAtNum =
      typeof row.lastReadAt === "number" && Number.isFinite(row.lastReadAt)
        ? row.lastReadAt
        : typeof row.readAt === "number" && Number.isFinite(row.readAt)
          ? row.readAt
          : null;
    items.push({
      id,
      latestAt: latestAtNum ?? slackTsToMs(latestTs),
      latestTs,
      latestAuthor: pickString(row.latestAuthor) || undefined,
      latestText: pickString(row.latestText) || undefined,
      readAt: readAtNum ?? slackTsToMs(row.lastRead),
      unread: row.unread === true,
      unreadCount:
        row.unread === true && typeof row.unreadCount === "number" && Number.isFinite(row.unreadCount)
          ? Math.max(0, Math.floor(row.unreadCount))
          : 0,
    });
  }
  return {
    accountId,
    supported: raw.supported !== false,
    mode,
    complete: raw.complete !== false,
    checked: Number(raw.checked) || 0,
    total: Number(raw.total) || 0,
    selfHandle: pickString(raw.selfHandle) || undefined,
    refreshedAt: pickString(raw.refreshedAt) || undefined,
    items,
  };
}

/** Mark read up to `ts` — Slack `conversations.mark` when allowed, and always in Hub. */
export async function markChatRead(accountId: string, channelId: string, ts: string): Promise<void> {
  await api<unknown>(
    `/api/chat/channels/${encodeURIComponent(channelId)}/read?accountId=${encodeURIComponent(accountId)}`,
    { method: "POST", body: JSON.stringify({ accountId, ts }) },
  );
}

/** Slack names group DMs `mpdm-ann--bo--you-1`; show the other people instead. */
export function chatGroupTitle(name: string, selfHandle?: string): string {
  const match = /^mpdm-(.+?)(?:-\d+)?$/.exec(name);
  if (!match) return name;
  const handles = match[1]!.split("--").filter(Boolean);
  const others = selfHandle ? handles.filter((handle) => handle !== selfHandle) : handles;
  return (others.length ? others : handles).join(", ");
}

export type ChatGifResult = { gifs: ChatGif[]; configured: boolean; message?: string };

/** Giphy search / trending via the Hub API proxy (`GET /api/chat/gifs`); the key stays server-side. */
export async function fetchChatGifs(query: string, offset = 0): Promise<ChatGifResult> {
  const qs = new URLSearchParams({ limit: "24", offset: String(offset) });
  if (query.trim()) qs.set("q", query.trim());
  try {
    const payload = await api<{ gifs?: ChatGif[]; configured?: boolean; message?: string }>(
      `/api/chat/gifs?${qs}`,
    );
    return {
      gifs: (payload.gifs || []).filter((g) => g && g.url && g.previewUrl),
      configured: payload.configured !== false,
      message: payload.message,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      return { gifs: [], configured: false, message: err.message || "GIF search is not configured." };
    }
    if (err instanceof ApiError && err.status === 404) {
      return { gifs: [], configured: false, message: "GIF search is not on the Hub API yet." };
    }
    throw err;
  }
}

/** Messages for one conversation — Lightsail `GET /api/chat/channels/:id/messages?accountId=`. */
export type ChatThreadSnapshot = {
  messages: ChatMessage[];
  /** People currently typing in this conversation, from the connector. */
  typing: string[];
};

export async function fetchChatThread(
  accountId: string,
  channelId: string,
): Promise<ChatThreadSnapshot> {
  const payload = await api<unknown>(
    `/api/chat/channels/${encodeURIComponent(channelId)}/messages?accountId=${encodeURIComponent(accountId)}`,
  );
  const list = Array.isArray(payload)
    ? payload
    : payload && typeof payload === "object"
      ? ((payload as { messages?: unknown[]; items?: unknown[] }).messages ||
        (payload as { items?: unknown[] }).items ||
        [])
      : [];
  const messages = list
    .map((raw, index) => normalizeChatMessage(raw, index))
    .filter((m): m is ChatMessage => Boolean(m))
    .sort((a, b) => (a.at ?? 0) - (b.at ?? 0));
  return { messages, typing: typingNamesFrom(payload) };
}

export async function fetchChatMessages(
  accountId: string,
  channelId: string,
): Promise<ChatMessage[]> {
  return (await fetchChatThread(accountId, channelId)).messages;
}

/**
 * Start OAuth for Slack or Cliq. Always additive — a second Connect keeps the first.
 * `forceLogin` asks the provider for an account picker when supported.
 */
export async function startChatConnect(
  providerId: ChatProviderId,
  opts?: { forceLogin?: boolean },
): Promise<ConnectResponse> {
  if (providerId === "google") {
    return {
      ok: false,
      message: "Connect Google Chat from Connections. Choose Chat in the Google account dialog.",
    };
  }
  const path =
    providerId === "cliq"
      ? "/api/integrations/cliq/connect"
      : providerId === "zoom"
        ? "/api/integrations/zoom-chat/connect"
        : providerId === "teams"
          ? "/api/integrations/microsoft-chat/connect"
          : "/api/integrations/slack/connect";
  const body: Record<string, unknown> = { addAccount: true };
  if (opts?.forceLogin) body.forceLogin = true;
  return api<ConnectResponse>(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function disconnectChatAccount(
  accountId: string,
): Promise<{ message?: string; ok?: boolean }> {
  return api<{ message?: string; ok?: boolean }>(
    `/api/chat/accounts/${encodeURIComponent(accountId)}/disconnect`,
    { method: "POST" },
  );
}
